import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL, SUB_ROLE_LABEL } from "@/constants/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useT } from "@/i18n/LanguageContext";

// Team management page — visible to Administrator + Manager (and Super Admin).
// Employees are blocked at the API level too, but we hide the link entirely.
export default function Team() {
  const { user } = useAuth();
  const { t } = useT();
  const qc = useQueryClient();
  const isSuper = user.role === "super_admin";
  const isAdmin = isSuper || user.sub_role === "administrator" || !user.sub_role;
  const isManager = user.sub_role === "manager";
  const canManage = isSuper || isAdmin || isManager;

  const [openAdd, setOpenAdd] = React.useState(false);
  const [editUser, setEditUser] = React.useState(null); // holds the user being edited

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["team"],
    queryFn: async () => (await api.get("/team")).data,
    enabled: canManage,
  });

  const { data: tabsResp } = useQuery({
    queryKey: ["team-tabs"],
    queryFn: async () => (await api.get("/team/tabs")).data,
    enabled: canManage,
  });

  // For non-super, tabsResp = { station, tabs }. For super, tabsResp = { stations: {...} }.
  const tabs = tabsResp?.tabs || [];
  const station = tabsResp?.station || user.role;

  const del = useMutation({
    mutationFn: async (uid) => (await api.delete(`/team/${uid}`)).data,
    onSuccess: () => { toast.success("Member removed"); qc.invalidateQueries({ queryKey: ["team"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  if (!canManage) {
    return (
      <div className="max-w-md" data-testid="team-forbidden">
        <h1 className="font-display text-2xl font-bold mb-2">{t("Team")}</h1>
        <p className="text-sm text-slate-500">
          You don't have permission to manage the team. Ask your station Administrator or Manager.
        </p>
      </div>
    );
  }

  const sortedMembers = [...members].sort((a, b) => {
    const order = { administrator: 0, manager: 1, employee: 2, null: 3 };
    return (order[a.sub_role || "null"] ?? 4) - (order[b.sub_role || "null"] ?? 4);
  });

  return (
    <div className="max-w-5xl space-y-6" data-testid="team-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold" data-testid="team-title">{t("Team")}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isSuper ? (
              <>Manage users across all stations · {members.length} accounts</>
            ) : (
              <>Station: <span className="font-medium text-slate-700">{t(ROLE_LABEL[station])}</span> · {members.length} accounts</>
            )}
          </p>
        </div>
        <Button
          onClick={() => setOpenAdd(true)}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="team-add-btn"
        >
          + Add Team Member
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-500 py-6">Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500 font-mono">
              <tr>
                <th className="text-left px-4 py-3">{t("Name")}</th>
                <th className="text-left px-4 py-3">{t("Contact")}</th>
                <th className="text-left px-4 py-3">{t("Tier")}</th>
                {isSuper && <th className="text-left px-4 py-3">{t("Station")}</th>}
                <th className="text-left px-4 py-3">{t("Access")}</th>
                <th className="text-right px-4 py-3">{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map((m) => (
                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`team-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium">{m.name}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                    {m.email && <div>{m.email}</div>}
                    {m.phone && <div className="text-slate-500">{m.phone}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <SubRoleBadge sub_role={m.sub_role} role={m.role} />
                  </td>
                  {isSuper && (
                    <td className="px-4 py-3 text-xs font-mono text-slate-600">{ROLE_LABEL[m.role] || m.role}</td>
                  )}
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px]">
                    {m.sub_role === "administrator" || !m.sub_role ? (
                      <span className="italic">All tabs</span>
                    ) : m.permissions == null ? (
                      <span className="italic">All tabs</span>
                    ) : (
                      <span data-testid={`team-perms-${m.id}`}>
                        {Object.values(m.permissions).filter(Boolean).length} of {tabs.length} tabs
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {m.id === user.id ? (
                      <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">You</span>
                    ) : m.sub_role === "administrator" && !isSuper ? (
                      <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Admin</span>
                    ) : (
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditUser(m)}
                          data-testid={`team-edit-${m.id}`}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (confirm(`Delete ${m.name}? This can't be undone.`)) del.mutate(m.id);
                          }}
                          className="text-red-700 border-red-200 hover:bg-red-50"
                          data-testid={`team-delete-${m.id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {sortedMembers.length === 0 && (
                <tr><td colSpan={isSuper ? 6 : 5} className="text-center text-slate-500 text-sm py-8">No team members yet. Click "Add Team Member" to invite one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <TeamMemberDialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        tabs={tabs}
        canAssignManager={isSuper || isAdmin}
        mode="create"
        onSaved={() => { setOpenAdd(false); qc.invalidateQueries({ queryKey: ["team"] }); }}
      />
      <TeamMemberDialog
        open={!!editUser}
        onClose={() => setEditUser(null)}
        tabs={tabs}
        canAssignManager={isSuper || isAdmin}
        mode="edit"
        initial={editUser}
        onSaved={() => { setEditUser(null); qc.invalidateQueries({ queryKey: ["team"] }); }}
      />
    </div>
  );
}

function SubRoleBadge({ sub_role, role }) {
  if (role === "super_admin") return <Badge className="bg-slate-900 text-white">Super Admin</Badge>;
  const map = {
    administrator: "bg-indigo-100 text-indigo-800 border border-indigo-200",
    manager: "bg-blue-100 text-blue-800 border border-blue-200",
    employee: "bg-slate-100 text-slate-700 border border-slate-200",
  };
  return <Badge className={map[sub_role] || "bg-slate-100 text-slate-500"}>{SUB_ROLE_LABEL[sub_role] || "—"}</Badge>;
}

function TeamMemberDialog({ open, onClose, tabs, canAssignManager, mode, initial, onSaved }) {
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      if (mode === "edit" && initial) {
        setForm({
          name: initial.name || "",
          email: initial.email || "",
          phone: initial.phone || "",
          password: "",
          sub_role: initial.sub_role || "employee",
          permissions: initial.permissions || {},
          _perms_all: initial.sub_role === "administrator" || initial.permissions == null,
        });
      } else {
        setForm({
          name: "", email: "", phone: "", password: "",
          sub_role: "employee",
          permissions: {},
          _perms_all: false,
        });
      }
    }
  }, [open, mode, initial]);

  const togglePerm = (key) => {
    setForm((f) => ({ ...f, permissions: { ...(f.permissions || {}), [key]: !f.permissions?.[key] } }));
  };
  const allOn = () => setForm((f) => ({ ...f, permissions: Object.fromEntries(tabs.map((t) => [t.key, true])) }));
  const allOff = () => setForm((f) => ({ ...f, permissions: {} }));

  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    if (mode === "create") {
      if (!form.email?.trim() && !form.phone?.trim()) return toast.error("Email or Phone is required");
      if (!form.password?.trim()) return toast.error("Password is required");
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email?.trim() || null,
        phone: form.phone?.trim() || null,
        sub_role: form.sub_role,
        permissions: form.sub_role === "administrator" || form._perms_all ? null : (form.permissions || {}),
      };
      if (form.password) payload.password = form.password;
      if (mode === "create") {
        await api.post("/team", payload);
        toast.success(`${form.sub_role === "manager" ? "Manager" : "Employee"} added`);
      } else {
        await api.patch(`/team/${initial.id}`, payload);
        toast.success("Saved");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="team-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Add Team Member" : `Edit ${initial?.name}`}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Full Name</Label>
              <Input
                data-testid="team-form-name"
                value={form.name || ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Tier</Label>
              <select
                data-testid="team-form-subrole"
                value={form.sub_role || "employee"}
                onChange={(e) => setForm({ ...form, sub_role: e.target.value })}
                className="mt-1 w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white"
                disabled={mode === "edit" && initial?.sub_role === "administrator"}
              >
                {canAssignManager && <option value="manager">Manager</option>}
                <option value="employee">Employee</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                data-testid="team-form-email"
                type="email"
                placeholder="you@example.com"
                value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input
                data-testid="team-form-phone"
                placeholder="+1 555 123 4567"
                value={form.phone || ""}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>
          <p className="text-[11px] text-slate-500 -mt-2">Provide at least one — user signs in with whichever they prefer.</p>

          <div>
            <Label className="text-xs">{mode === "edit" ? "New Password (leave empty to keep)" : "Password"}</Label>
            <Input
              data-testid="team-form-password"
              type="password"
              value={form.password || ""}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1"
            />
          </div>

          {/* Permissions grid */}
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium text-slate-700">Tab Access</div>
              <div className="flex items-center gap-3">
                <label className="text-[11px] flex items-center gap-1.5 text-slate-600 cursor-pointer" data-testid="team-form-permall">
                  <input
                    type="checkbox"
                    checked={!!form._perms_all}
                    onChange={(e) => setForm({ ...form, _perms_all: e.target.checked })}
                  />
                  All tabs
                </label>
                <button type="button" onClick={allOn} className="text-[11px] text-slate-500 hover:text-slate-700 underline">Select all</button>
                <button type="button" onClick={allOff} className="text-[11px] text-slate-500 hover:text-slate-700 underline">Clear</button>
              </div>
            </div>
            {form._perms_all ? (
              <p className="text-[11px] text-slate-500 italic">This member will see every tab in the station.</p>
            ) : (
              <div className="grid grid-cols-2 gap-1.5 max-h-[180px] overflow-y-auto">
                {tabs.map((t) => (
                  <label
                    key={t.key}
                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white cursor-pointer text-xs"
                    data-testid={`team-form-perm-${t.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!form.permissions?.[t.key]}
                      onChange={() => togglePerm(t.key)}
                    />
                    <span>{t.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="team-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="team-form-save">
            {saving ? "Saving…" : mode === "create" ? "Add member" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
